import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const About = () => {
  const values = [
    'Respect for your home',
    'Clear communication',
    'Reliable scheduling'
  ];

  const reasons = [
    'Professional crew',
    'Protecting floors & furniture',
    'Simple quoting and confirmation',
    'Friendly, respectful service'
  ];

  const serviceAreas = [
    'Vancouver', 'Surrey', 'Delta', 'Langley', 'Burnaby', 
    'Abbotsford', 'Chilliwack', 'Mission', 'Vancouver Island', 'Victoria'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 animate-fadeInUp">
            A moving team you can trust.
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed animate-fadeInUp animation-delay-100">
            At BDC Movers, we understand that moving is more than just transporting belongings—it's about handling your life with care. Our professional team is committed to making your move smooth, reliable, and stress-free with clear communication every step of the way.
          </p>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 text-center animate-fadeInUp">
              Our Values
            </h2>
            <div className="space-y-4">
              {values.map((value, index) => (
                <div 
                  key={index} 
                  className="flex items-center bg-gray-50 rounded-lg p-4 hover:bg-orange-50 hover:shadow-md transition-all duration-300 animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <CheckCircle className="text-orange-500 mr-4 flex-shrink-0" size={24} />
                  <p className="text-lg text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose BDC */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center animate-fadeInUp">
              Why Choose BDC Movers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reasons.map((reason, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-lg p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-start">
                    <CheckCircle className="text-orange-500 mr-3 flex-shrink-0 mt-1" size={20} />
                    <p className="text-gray-900 font-medium">{reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
              Serving British Columbia
            </h2>
            <div className="bg-gray-50 rounded-lg p-8">
              <MapPin className="text-orange-500 mx-auto mb-4" size={32} />
              <div className="flex flex-wrap justify-center gap-3">
                {serviceAreas.map((area, index) => (
                  <span
                    key={index}
                    className="inline-block bg-white px-4 py-2 rounded-full text-gray-900 font-medium text-sm"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to experience professional moving?
          </h2>
          <p className="text-xl mb-8">
            Get your free quote today
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-orange-500 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105"
          >
            Get a Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
